import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  DiscountElementTargetType,
  DiscountElementType,
  DiscountGroupStatus,
  DraftLineDiscountStatusV2,
} from "@erp/shared-types";

/**
 * Discounts v2 contracts — the OMS v3 discount system.
 *
 * Groups (exception price lists / bulk batches / SPRs) hold elements; an
 * engine matches elements to draft sales-order lines and stacks them in a
 * deterministic application order. See `.claude/skills/discounts`.
 */

const UUIDSchema = z.uuid();

const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

export const DiscountElementTypeSchema = z.nativeEnum(DiscountElementType);
export const DiscountElementTargetTypeSchema = z.nativeEnum(
  DiscountElementTargetType,
);
export const DiscountGroupStatusSchema = z.nativeEnum(DiscountGroupStatus);
export const DraftLineDiscountStatusV2Schema = z.nativeEnum(
  DraftLineDiscountStatusV2,
);

/** Money/quantity values cross the wire as canonical decimal strings. */
const DecimalStringSchema = z.string();

// ─── Element / group DTOs ───────────────────────────────────────────────────

export const DiscountElementV2Schema = z.object({
  id: UUIDSchema,
  groupId: UUIDSchema.nullable(),
  customerId: UUIDSchema.nullable(),
  discountType: DiscountElementTypeSchema,
  discountValue: DecimalStringSchema,
  targetType: DiscountElementTargetTypeSchema,
  targetValue: z.string().nullable(),
  /** Human-readable target (resolved SKU for variant targets). */
  targetLabel: z.string().nullable(),
  effectiveStart: DateOnlySchema.nullable(),
  effectiveEnd: DateOnlySchema.nullable(),
  isActive: z.boolean(),
  isSingleUse: z.boolean(),
  isConsumed: z.boolean(),
  maxQuantity: DecimalStringSchema.nullable(),
  usedQuantity: DecimalStringSchema,
  createdAt: z.iso.datetime(),
});
export type DiscountElementV2 = z.infer<typeof DiscountElementV2Schema>;

export const DiscountGroupV2Schema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  referenceCode: z.string().nullable(),
  /** Generated shareable code (e.g. "X7K9-Q24M") a customer can quote. */
  discountCode: z.string().nullable(),
  status: DiscountGroupStatusSchema,
  customerId: UUIDSchema.nullable(),
  customerName: z.string().nullable(),
  attachment: z.string().nullable(),
  createdByName: z.string().nullable(),
  elementCount: z.number().int(),
  activeElementCount: z.number().int(),
  /** True when metadata.review still has unresolved customer/lines. */
  needsReview: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type DiscountGroupV2 = z.infer<typeof DiscountGroupV2Schema>;

// ─── Review (ingested-group fix-up) ─────────────────────────────────────────

export const DiscountGroupReviewCandidateSchema = z.object({
  productVariantId: UUIDSchema,
  confidence: z.number(),
  sku: z.string().nullable(),
  label: z.string().nullable(),
});

export const DiscountGroupReviewLineSchema = z.object({
  key: z.string(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  discountType: DiscountElementTypeSchema,
  discountValue: z.number(),
  maxQuantity: z.number().nullable(),
  effectiveStart: z.string().nullable(),
  effectiveEnd: z.string().nullable(),
  unresolvedReason: z.enum(["sku_not_matched", "no_price_or_percent"]),
  candidates: z.array(DiscountGroupReviewCandidateSchema),
});
export type DiscountGroupReviewLineDTO = z.infer<
  typeof DiscountGroupReviewLineSchema
>;

export const DiscountGroupReviewSchema = z.object({
  suggestedCustomer: z
    .object({
      customerId: UUIDSchema,
      customerName: z.string(),
      score: z.number(),
    })
    .optional(),
  unresolvedCustomerName: z.string().nullable().optional(),
  unresolvedLines: z.array(DiscountGroupReviewLineSchema),
  dismissedLines: z.array(DiscountGroupReviewLineSchema).optional(),
});
export type DiscountGroupReviewDTO = z.infer<typeof DiscountGroupReviewSchema>;

export const DiscountGroupV2DetailSchema = DiscountGroupV2Schema.extend({
  elements: z.array(DiscountElementV2Schema),
  /** Human fix-up state for ingested groups; null once everything resolved. */
  review: DiscountGroupReviewSchema.nullable(),
  /**
   * Sender of the email this request was ingested from, when there was one —
   * drives the "Approve & Email" reply-to-requester flow. Null for manually
   * created / uploaded groups.
   */
  requesterEmail: z.string().nullable(),
});
export type DiscountGroupV2Detail = z.infer<typeof DiscountGroupV2DetailSchema>;

// ─── Group CRUD ──────────────────────────────────────────────────────────────

/** Sortable columns on the Discounts page group list (server-side sort). */
export const DiscountGroupSortBySchema = z.enum([
  "name",
  "discountCode",
  "customerName",
  "status",
  "createdByName",
  "createdAt",
]);
export type DiscountGroupSortBy = z.infer<typeof DiscountGroupSortBySchema>;

export const DiscountGroupSortDirectionSchema = z.enum(["asc", "desc"]);

export const ListDiscountGroupsInputSchema = z.object({
  search: z.string().optional(),
  status: z.array(DiscountGroupStatusSchema).optional(),
  customerId: UUIDSchema.optional(),
  /** Defaults to newest-first when omitted. */
  sortBy: DiscountGroupSortBySchema.optional(),
  sortDirection: DiscountGroupSortDirectionSchema.optional(),
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
});

export const ListDiscountGroupsResponseSchema = z.object({
  groups: z.array(DiscountGroupV2Schema),
  pagination: z.object({
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  }),
});

export const GetDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

/** Source-email descriptor for the review page's Emails rail tab. */
export const GroupSourceEmailThreadSchema = z.object({
  /** Whether the group was created from an inbound email. */
  hasSourceEmail: z.boolean(),
  /** Graph conversation/thread id — feeds the email-thread viewer. */
  conversationId: z.string().nullable(),
  /** The source inbound email id (for highlighting in the thread). */
  sourceEmailId: UUIDSchema.nullable(),
  /** Sender of the source email (seeds the reply composer's To). */
  fromAddress: z.string().nullable(),
  /** Subject of the source email. */
  subject: z.string().nullable(),
});

// ─── Activity (per-group action timeline) ───────────────────────────────────

/** One recorded action on a group — creation, edits, status changes, … */
export const DiscountGroupActivityEntrySchema = z.object({
  /** Namespaced kind, e.g. "discount_group.approved". */
  kind: z.string(),
  at: z.iso.datetime(),
  actorUserId: UUIDSchema.nullable(),
  /** Resolved user name, or a system-actor label ("Approval workflow"). */
  actorName: z.string().nullable(),
  message: z.string().nullable(),
  /** Per-field diff: field → [before, after] (ids resolved to names). */
  valuesChanged: z.record(z.string(), z.unknown()).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});
export type DiscountGroupActivityEntry = z.infer<
  typeof DiscountGroupActivityEntrySchema
>;

export const GetDiscountGroupActivityResponseSchema = z.object({
  entries: z.array(DiscountGroupActivityEntrySchema),
});

// ─── Usage (which sales orders a group's / element's discounts were applied to)

/** At least one of the two ids must be provided (refined in the handler). */
export const GetDiscountUsageInputSchema = z.object({
  discountGroupId: UUIDSchema.optional(),
  discountElementId: UUIDSchema.optional(),
});

/** One sales order that had this discount frozen onto at least one of its lines. */
export const DiscountUsageRowSchema = z.object({
  salesOrderId: UUIDSchema,
  /** The originating draft, for deep-linking to the sales-order-v3 page. */
  draftSalesOrderId: UUIDSchema.nullable(),
  /** Auto-increment human order number. */
  internalId: z.number().int(),
  customerPurchaseOrderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  orderDate: DateOnlySchema.nullable(),
  totalAmount: DecimalStringSchema.nullable(),
  /** How many of this order's lines carry one of the queried elements. */
  lineCount: z.number().int(),
});
export type DiscountUsageRow = z.infer<typeof DiscountUsageRowSchema>;

export const GetDiscountUsageResponseSchema = z.object({
  usage: z.array(DiscountUsageRowSchema),
});

// ─── Flat element list (the "Elements" view of the Discounts page) ──────────

/** One element row with its group context, for the flat elements table. */
export const DiscountElementListItemSchema = DiscountElementV2Schema.extend({
  groupName: z.string().nullable(),
  groupStatus: DiscountGroupStatusSchema.nullable(),
});
export type DiscountElementListItem = z.infer<
  typeof DiscountElementListItemSchema
>;

export const ListDiscountElementsInputSchema = z.object({
  /** Matches target SKU / segment value / group name or reference. */
  search: z.string().optional(),
  /** Elements that would apply to this customer (scoped to it or unscoped). */
  customerId: UUIDSchema.optional(),
  status: z.enum(["active", "inactive", "all"]).optional(),
  /** Only elements of this discount type. */
  discountType: DiscountElementTypeSchema.optional(),
  /**
   * PRODVIEW classification filters — same levels as the Product Catalog's
   * Family/Group/Model dropdowns (segments 1..3 of the dot-separated PRODVIEW).
   * Matches segment-targeted elements directly and variant-targeted elements
   * via their variant's PRODVIEW.
   */
  family: z.string().optional(),
  group: z.string().optional(),
  model: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
});

export const ListDiscountElementsResponseSchema = z.object({
  elements: z.array(DiscountElementListItemSchema),
  pagination: z.object({
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  }),
});

export const CreateDiscountElementInputSchema = z
  .object({
    discountType: DiscountElementTypeSchema,
    /**
     * Bps integer (e.g. 2050 = 20.50%) for PERCENT_BPS_OFF; currency amount
     * for AMOUNT_OFF / FIXED_PRICE.
     */
    discountValue: z.number().nonnegative(),
    targetType: DiscountElementTargetTypeSchema,
    /** Variant id / PRODVIEW segment; omit for ALL_PRODUCTS. */
    targetValue: z.string().trim().min(1).nullable().optional(),
    customerId: UUIDSchema.nullable().optional(),
    effectiveStart: DateOnlySchema.nullable().optional(),
    effectiveEnd: DateOnlySchema.nullable().optional(),
    isSingleUse: z.boolean().optional(),
    maxQuantity: z.number().positive().nullable().optional(),
  })
  .superRefine((el, ctx) => {
    if (
      el.targetType === DiscountElementTargetType.ALL_PRODUCTS &&
      el.targetValue
    ) {
      ctx.addIssue({
        code: "custom",
        message: "ALL_PRODUCTS elements must not carry a targetValue",
        path: ["targetValue"],
      });
    }
    if (
      el.targetType !== DiscountElementTargetType.ALL_PRODUCTS &&
      !el.targetValue
    ) {
      ctx.addIssue({
        code: "custom",
        message: `${el.targetType} elements require a targetValue`,
        path: ["targetValue"],
      });
    }
    if (
      el.discountType === DiscountElementType.PERCENT_BPS_OFF &&
      (!Number.isInteger(el.discountValue) ||
        el.discountValue <= 0 ||
        el.discountValue > 10000)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "PERCENT_BPS_OFF requires an integer bps in (0, 10000]",
        path: ["discountValue"],
      });
    }
    if (
      el.effectiveStart &&
      el.effectiveEnd &&
      el.effectiveEnd < el.effectiveStart
    ) {
      ctx.addIssue({
        code: "custom",
        message: "effectiveEnd must be on or after effectiveStart",
        path: ["effectiveEnd"],
      });
    }
  });
export type CreateDiscountElementInput = z.infer<
  typeof CreateDiscountElementInputSchema
>;

export const CreateDiscountGroupInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).nullable().optional(),
  referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
  customerId: UUIDSchema.nullable().optional(),
  attachment: z.string().nullable().optional(),
  elements: z.array(CreateDiscountElementInputSchema).min(1),
  /** Submit straight to approval instead of leaving the group in DRAFT. */
  submitForApproval: z.boolean().optional(),
  /**
   * Create APPROVED with active elements — for flows where the creator has
   * the authority (e.g. the sales-order review screen, where the order itself
   * goes through approval). Mutually exclusive with `submitForApproval`.
   */
  approveImmediately: z.boolean().optional(),
});
export type CreateDiscountGroupInput = z.infer<
  typeof CreateDiscountGroupInputSchema
>;

export const CreateDiscountGroupResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
});

export const SubmitGroupForApprovalInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

/** Direct RBAC-backed decision (requires the `approve` action on DiscountV2). */
export const DecideDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
  decision: z.enum([
    DiscountGroupStatus.APPROVED,
    DiscountGroupStatus.REJECTED,
  ]),
});

export const DecideDiscountGroupResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
  activatedElementCount: z.number().int(),
});

export const SubmitGroupForApprovalResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
});

/**
 * One-click "Approve & Email": approve + reply to the requester with the
 * approved form attached, Cc'ing the approver. Fails without approving when
 * the org has no discounts default sending mailbox configured.
 */
export const ApproveAndEmailDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const ApproveAndEmailDiscountGroupResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
  activatedElementCount: z.number().int(),
  /** Requester address the approval email was sent to. */
  emailedTo: z.string(),
  /** Everyone Cc'd (discounts default Cc list + the approver). */
  cc: z.array(z.string()),
});

export const DeactivateDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const DeleteDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const RevertDiscountGroupToDraftInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

/** A generated Excel workbook, base64-encoded for the JSON transport. */
export const DiscountFormFileSchema = z.object({
  excelBase64: z.string(),
  filename: z.string(),
});

export const GenerateApprovedDiscountFormInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const GenerateApprovedDiscountFormResponseSchema =
  DiscountFormFileSchema.extend({
    /**
     * Best-known requester address to prefill the compose panel with — the
     * ingested email's sender when the group came in by email, else null.
     */
    suggestedRecipient: z.string().nullable(),
    /**
     * The generated form persisted as a document (associated to the group) —
     * lets the frontend render it with the real Office spreadsheet viewer.
     * Null when the upload failed: the workbook still comes back inline, so
     * the preview falls back to the in-browser renderer.
     */
    documentId: UUIDSchema.nullable(),
  });

export const DeactivateDiscountElementInputSchema = z.object({
  discountElementId: UUIDSchema,
});

/** Element editing (review page): allowed only on DRAFT/REJECTED groups. */
export const AddDiscountElementInputSchema = z.object({
  discountGroupId: UUIDSchema,
  element: CreateDiscountElementInputSchema,
});
export const AddDiscountElementResponseSchema = z.object({
  discountElementId: UUIDSchema,
});

export const UpdateDiscountElementInputSchema = z.object({
  discountGroupId: UUIDSchema,
  discountElementId: UUIDSchema,
  element: CreateDiscountElementInputSchema,
});

export const DeleteDiscountElementInputSchema = z.object({
  discountGroupId: UUIDSchema,
  discountElementId: UUIDSchema,
});

export const SimpleSuccessResponseSchema = z.object({
  success: z.boolean(),
});

export const UpdateDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
  patch: z
    .object({
      name: z.string().trim().min(1).max(255).optional(),
      description: z.string().trim().max(2000).nullable().optional(),
      referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
      customerId: UUIDSchema.nullable().optional(),
    })
    .refine((patch) => Object.keys(patch).length > 0, {
      message: "Patch must contain at least one field",
    }),
});

export const ResolveUnresolvedLineInputSchema = z.object({
  discountGroupId: UUIDSchema,
  lineKey: z.string().min(1),
  productVariantId: UUIDSchema,
});

export const DismissUnresolvedLineInputSchema = z.object({
  discountGroupId: UUIDSchema,
  lineKey: z.string().min(1),
});

export const ParsePriceListUploadInputSchema = z.object({
  /** An uploaded + confirmed document (xlsx/xls/csv). */
  documentId: UUIDSchema,
  name: z.string().trim().min(1).max(255).nullable().optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
  customerId: UUIDSchema.nullable().optional(),
  effectiveStart: DateOnlySchema.nullable().optional(),
  effectiveEnd: DateOnlySchema.nullable().optional(),
  /** Submit straight to approval when nothing needs review. */
  submitForApproval: z.boolean().optional(),
  /**
   * Create APPROVED with active elements when nothing needs review ("Save
   * And Activate"). Mutually exclusive with submitForApproval.
   */
  approveImmediately: z.boolean().optional(),
});
export type ParsePriceListUploadInput = z.infer<
  typeof ParsePriceListUploadInputSchema
>;

export const ParsePriceListUploadResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
  elementCount: z.number().int(),
  unresolvedCount: z.number().int(),
  /** SKUs (as written in the sheet) that could not be matched to a product. */
  unresolvedSkus: z.array(z.string()),
  /** True when the sheet exceeded the row cap and was cut off. */
  truncated: z.boolean(),
});
export type ParsePriceListUploadResponse = z.infer<
  typeof ParsePriceListUploadResponseSchema
>;

// ─── Draft-line candidates / stacks ─────────────────────────────────────────

export const DiscountCandidateV2Schema = DiscountElementV2Schema.extend({
  groupName: z.string().nullable(),
  groupStatus: DiscountGroupStatusSchema.nullable(),
  /** True when the element's group is still awaiting approval. */
  pending: z.boolean(),
  /** Discount request end customer (group metadata), when the grant names one. */
  endCustomerName: z.string().nullable(),
});
export type DiscountCandidateV2 = z.infer<typeof DiscountCandidateV2Schema>;

export const DraftLineCandidatesSchema = z.object({
  draftLineId: UUIDSchema,
  candidates: z.array(DiscountCandidateV2Schema),
  /**
   * Element the engine would auto-apply (a single active FIXED_PRICE candidate
   * exactly matching the PO unit price), when one exists.
   */
  autoApplyElementId: UUIDSchema.nullable(),
});

export const ListDraftCandidatesInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
});

export const ListDraftCandidatesResponseSchema = z.object({
  found: z.boolean(),
  lines: z.array(DraftLineCandidatesSchema),
});

export const DiscountStackTraceEntrySchema = z.object({
  elementId: UUIDSchema,
  applicationOrder: z.number().int(),
  discountType: DiscountElementTypeSchema,
  discountValue: DecimalStringSchema,
  unitPriceBefore: DecimalStringSchema,
  unitPriceAfter: DecimalStringSchema,
  discountAmount: DecimalStringSchema,
});

export const DraftLineStackSchema = z.object({
  draftLineDiscountId: UUIDSchema,
  draftLineId: UUIDSchema,
  totalDiscountAmount: DecimalStringSchema,
  finalUnitPrice: DecimalStringSchema.nullable(),
  /** True when any element's group is still awaiting approval. */
  pending: z.boolean(),
  elements: z.array(DiscountCandidateV2Schema),
  trace: z.array(DiscountStackTraceEntrySchema).nullable(),
});
export type DraftLineStack = z.infer<typeof DraftLineStackSchema>;

export const GetDraftLineStacksInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
});

export const GetDraftLineStacksResponseSchema = z.object({
  found: z.boolean(),
  stacks: z.array(DraftLineStackSchema),
});

export const ApplyLineStackInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
  draftLineId: UUIDSchema,
  /** Elements to stack; the engine assigns the canonical application order. */
  elementIds: z.array(UUIDSchema).min(1),
});

export const ApplyLineStackResponseSchema = z.object({
  stack: DraftLineStackSchema,
});

/**
 * Batch apply: one call for a whole order's worth of stacks (the bulk-discount
 * flow). Applying line-by-line would emit one approval-recheck event per line
 * — the whole approver team got re-notified once per line item.
 */
export const ApplyLineStacksInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
  applications: z
    .array(
      z.object({
        draftLineId: UUIDSchema,
        elementIds: z.array(UUIDSchema).min(1),
      }),
    )
    .min(1),
});

export const ApplyLineStacksResponseSchema = z.object({
  /** Per-line outcomes, in input order; a failed line never fails the batch. */
  results: z.array(
    z.object({
      draftLineId: UUIDSchema,
      success: z.boolean(),
      errorMessage: z.string().nullable(),
    }),
  ),
});

export const RemoveLineStackInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
  draftLineId: UUIDSchema,
});

// ─── Per-line validation (review page status column) ───────────────────────

export const DraftLineDiscountValidationV2Schema = z.object({
  draftLineId: UUIDSchema,
  status: DraftLineDiscountStatusV2Schema,
  listPrice: DecimalStringSchema.nullable(),
  poUnitPrice: DecimalStringSchema.nullable(),
  /** Final unit price the applied stack yields, when a stack exists. */
  stackFinalUnitPrice: DecimalStringSchema.nullable(),
  draftLineDiscountId: UUIDSchema.nullable(),
});
export type DraftLineDiscountValidationV2 = z.infer<
  typeof DraftLineDiscountValidationV2Schema
>;

export const ValidateDraftDiscountsV2InputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
});

export const ValidateDraftDiscountsV2ResponseSchema = z.object({
  found: z.boolean(),
  results: z.record(z.string(), DraftLineDiscountValidationV2Schema),
});

// ─── Contracts ───────────────────────────────────────────────────────────────

export const discountsV2Contracts = {
  listGroups: oc
    .input(ListDiscountGroupsInputSchema)
    .output(ListDiscountGroupsResponseSchema)
    .route({ method: "GET", path: "/groups" }),
  getGroup: oc
    .input(GetDiscountGroupInputSchema)
    .output(DiscountGroupV2DetailSchema)
    .route({ method: "GET", path: "/group" }),
  getActivity: oc
    .input(GetDiscountGroupInputSchema)
    .output(GetDiscountGroupActivityResponseSchema)
    .route({ method: "GET", path: "/group/activity" }),
  // Source-email descriptor for the review page's Emails rail tab. Resolves
  // the group (org-scoped) → its source InboundEmail, returning the
  // conversationId the existing email-thread viewer fetches the full thread
  // with, plus the sender + subject to seed a reply. hasSourceEmail is false
  // for groups not created from an email (quote builder, manual entry).
  getSourceEmailThread: oc
    .input(GetDiscountGroupInputSchema)
    .output(GroupSourceEmailThreadSchema)
    .route({ method: "GET", path: "/group/source-email-thread" }),
  getUsage: oc
    .input(GetDiscountUsageInputSchema)
    .output(GetDiscountUsageResponseSchema)
    .route({ method: "GET", path: "/usage" }),
  listElements: oc
    .input(ListDiscountElementsInputSchema)
    .output(ListDiscountElementsResponseSchema)
    .route({ method: "GET", path: "/elements" }),
  createGroup: oc
    .input(CreateDiscountGroupInputSchema)
    .output(CreateDiscountGroupResponseSchema)
    .route({ method: "POST", path: "/groups" }),
  decideGroup: oc
    .input(DecideDiscountGroupInputSchema)
    .output(DecideDiscountGroupResponseSchema)
    .route({ method: "POST", path: "/group/decide" }),
  approveAndEmail: oc
    .input(ApproveAndEmailDiscountGroupInputSchema)
    .output(ApproveAndEmailDiscountGroupResponseSchema)
    .route({ method: "POST", path: "/groups/approve-and-email" }),
  submitGroupForApproval: oc
    .input(SubmitGroupForApprovalInputSchema)
    .output(SubmitGroupForApprovalResponseSchema)
    .route({ method: "POST", path: "/groups/submit-for-approval" }),
  deactivateGroup: oc
    .input(DeactivateDiscountGroupInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/groups/deactivate" }),
  deleteGroup: oc
    .input(DeleteDiscountGroupInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "DELETE", path: "/groups" }),
  deactivateElement: oc
    .input(DeactivateDiscountElementInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/elements/deactivate" }),
  addElement: oc
    .input(AddDiscountElementInputSchema)
    .output(AddDiscountElementResponseSchema)
    .route({ method: "POST", path: "/elements/add" }),
  updateElement: oc
    .input(UpdateDiscountElementInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/elements/update" }),
  deleteElement: oc
    .input(DeleteDiscountElementInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "DELETE", path: "/elements" }),
  updateGroup: oc
    .input(UpdateDiscountGroupInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/groups/update" }),
  revertGroupToDraft: oc
    .input(RevertDiscountGroupToDraftInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/groups/revert-to-draft" }),
  getRequestFormTemplate: oc
    .output(DiscountFormFileSchema)
    .route({ method: "GET", path: "/request-form-template" }),
  generateApprovedForm: oc
    .input(GenerateApprovedDiscountFormInputSchema)
    .output(GenerateApprovedDiscountFormResponseSchema)
    .route({ method: "POST", path: "/groups/approved-form" }),
  resolveUnresolvedLine: oc
    .input(ResolveUnresolvedLineInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/groups/resolve-line" }),
  dismissUnresolvedLine: oc
    .input(DismissUnresolvedLineInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/groups/dismiss-line" }),
  parsePriceListUpload: oc
    .input(ParsePriceListUploadInputSchema)
    .output(ParsePriceListUploadResponseSchema)
    .route({ method: "POST", path: "/groups/parse-price-list" }),
  listDraftCandidates: oc
    .input(ListDraftCandidatesInputSchema)
    .output(ListDraftCandidatesResponseSchema)
    .route({ method: "GET", path: "/draft-candidates" }),
  getDraftLineStacks: oc
    .input(GetDraftLineStacksInputSchema)
    .output(GetDraftLineStacksResponseSchema)
    .route({ method: "GET", path: "/draft-line-stacks" }),
  applyLineStack: oc
    .input(ApplyLineStackInputSchema)
    .output(ApplyLineStackResponseSchema)
    .route({ method: "POST", path: "/apply-line-stack" }),
  applyLineStacks: oc
    .input(ApplyLineStacksInputSchema)
    .output(ApplyLineStacksResponseSchema)
    .route({ method: "POST", path: "/apply-line-stacks" }),
  removeLineStack: oc
    .input(RemoveLineStackInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({ method: "POST", path: "/remove-line-stack" }),
  validateDraftDiscounts: oc
    .input(ValidateDraftDiscountsV2InputSchema)
    .output(ValidateDraftDiscountsV2ResponseSchema)
    .route({ method: "GET", path: "/validate-draft" }),
};
